import TenantObserver, {
  TenantEvents
} from '../../../services/tenantHandler/TenantObserver';
import { ITenantHandlerService } from '../../../services/tenantHandler';
import { LastTenantIdsRepository } from './LastTenantIdsRepository';
import TenantRetriever from './TenantRetriever';
import TabHelpers from './Helpers';
import TenantSetter from './TenantSetter';
import { ISessionService } from '../../../services/session';
import IApplicationService from '../../../services/applicationService/IApplicationService';
import AuthenticationProviderEnum from '../../../config/authenticationProviderEnum';
import { getServices } from '../../../infra/startup';
import { internalLogger } from '../../../interface/v1/clientOsLogger';
import BaseLoginService from '../../../services/session/loginService/BaseLoginService';
import { INavigationService } from '../../../services/navigationService';
import { InitialTenantType } from './types';
import {
  AuthContextEnum,
  IAuthTokenService
} from '../../../services/authTokenService';
import { ILoginService } from '../../../services/session/loginService';

type MultipleTabsDeamonOptions = {
  repository: LastTenantIdsRepository;
  isNative: boolean;
};

export default class MultipleTabsDeamon {
  private _tenantHandlerService: ITenantHandlerService;
  private _repository: LastTenantIdsRepository;
  private _tenantRetriever: TenantRetriever;
  private _tenantSetter: TenantSetter;
  private _sessionService: ISessionService;
  private _applicationService: IApplicationService;
  private _isNative: boolean;
  private _queryParamsEnabled: boolean;
  private _navigationService: INavigationService;
  private _loginService: ILoginService;
  private _authTokenService: IAuthTokenService;

  constructor({ repository, isNative }: MultipleTabsDeamonOptions) {
    const {
      tenantHandlerService,
      sessionService,
      authTokenService,
      applicationService,
      navigationService,
      loginService
    } = getServices();

    this._tenantHandlerService = tenantHandlerService;
    this._sessionService = sessionService;
    this._applicationService = applicationService;
    this._repository = repository;
    this._isNative = isNative;
    this._navigationService = navigationService;
    this._loginService = loginService;
    this._authTokenService = authTokenService;

    // For Coptor, it is being disabled by default.
    this._queryParamsEnabled =
      this._applicationService.getAuthenticationProvider() !=
      AuthenticationProviderEnum.coptor;

    this._tenantSetter = new TenantSetter({
      navigationService,
      repository,
      queryParamsEnabled: this._queryParamsEnabled
    });
    this._tenantRetriever = new TenantRetriever({
      navigationService,
      tenantHandlerService,
      authTokenService,
      applicationService,
      repository,
      queryParamsEnabled: this._queryParamsEnabled
    });
  }

  private _isInLoginPath(): boolean {
    return BaseLoginService.isInLoginPath();
  }

  public async init(): Promise<void> {
    if (!this._tenantHandlerService?.isEnabled()) {
      return;
    }

    if (this._isNative) {
      /* istanbul ignore next */
      internalLogger?.debug?.('Multitab is disabled in Native Environment.');
      return;
    }

    /*
     * 1. When the user is not logged yet is expected the repository would be empty, based on cleanUpEnvironment behavior.
     **/

    const isLoggedIn = this._sessionService.isLoggedIn();

    /*
     * 2. Nevertheless, in /login path, if there are tenant IDs in the query parameters, we set these tenants IDs in the last tenant IDs repository and updates the URL removing the tenant IDs.

     * When queryParamsEnabled is false,
     * this._tenantRetriever.hasTenantOnQueryParams() will always return false.
    * */
    if (
      this._isInLoginPath() &&
      this._tenantRetriever.hasTenantOnQueryParams()
    ) {
      this.overrideReposityTenantsFromQueryParams();
      return;
    }

    /*
     * 2. When the user is logged in:
     *  a. Setting the Candidate Tenant
     *  b. Subscribe to the TenantEvents.SET_TENANT event to update the last tenant
     */
    if (isLoggedIn) {
      // Update the tenant params in the URL in ever navigation push.
      if (this._queryParamsEnabled) TabHelpers.old_persistTenantQueryParam();

      this.setInitialTenantBasedOnLastTenant();

      this._eventSubscriptions();
    }
  }

  /**
   * This is a feature to retrieve the tenant from QueryParams and setting it as priority over Repository. */
  private overrideReposityTenantsFromQueryParams = () => {
    const fromQueryparams =
      this._tenantRetriever.retrieveTenantListFromQueryParams();
    const fromRepo =
      this._tenantRetriever.retrieveLastTenantsIdFromRepository();
    const tenantIdList = this._tenantRetriever.mergeTenants(
      fromQueryparams,
      fromRepo
    );
    this._repository.save(tenantIdList);

    // Cleaning the QueryParams avoids the tenant IDs to be duplicated during the redirects to identity provider.
    // Disabling to force retry if the exchangeToken gets 400
    //TabHelpers.removeTenantsFromQueryParams();
  };

  private _eventSubscriptions = () => {
    // 1. When the SET_TENANT_HANDLER_KEY is called, the ClientOS is ready to set the tenant.
    TenantObserver.subscribe(
      TenantEvents.SET_TENANT_HANDLER_KEY,
      this.setTenantsFromInitialTenants
    );

    // 2. Subscribe to the TenantEvents.SET_TENANT event to receive updates when the tenant was set.
    TenantObserver.subscribe(
      TenantEvents.SET_TENANT,
      this._tenantSetter.cb_updateLastTenantData
    );
  };

  // Here we set the behavior for effectively set the tenant,
  // but ONLY when the jshell-commons finishes its routering.
  /*
   * Every time a page refresh ocourrs:
   * Case #1: If contains a tenant in URL (candidate tenant)
   *   It will be set as the initial tenant. In this case, the
   *   LastTenent Repository contains the last valid tenant.
   *
   * Case #2: If doesn't contain in URL and contains in Repository.
   *    Get from the Repository and set as the initial tenant.
   *
   * Case #3: if doesn't contain any tenant in URL and Repostory.
   *    The initial Tenant will be empty.
   *    For Web: The Tenant Selector Operation will handle it.
   *    For Native: We need to set the TenantId to be exchange (since there is no tenant selector)
   */
  private setInitialTenantBasedOnLastTenant() {
    let tenantIdList =
      this._tenantRetriever.retrieveTenantListFromQueryParams();

    if (!tenantIdList.length)
      tenantIdList =
        this._tenantRetriever.retrieveLastTenantsIdFromRepository();

    this._tenantHandlerService?.setInitialTenants(tenantIdList);
  }

  private _updateLastTenantOnRepositories(tenant: InitialTenantType): boolean {
    /* istanbul ignore next */
    internalLogger?.debug?.(
      'syncing our URL and last tenant repository with tenant id: ',
      tenant.id
    );
    this._tenantSetter.updateLastTenantOnURL([tenant]);
    this._tenantSetter.updateLastTenantOnRepository([tenant]);

    return true;
  }

  private async _userHasValidLastTenant(): Promise<boolean> {
    const tenantListFromRepo =
      this._tenantRetriever.retrieveLastTenantsIdFromRepository();

    if (!tenantListFromRepo?.length) {
      /* istanbul ignore next */
      internalLogger?.debug?.(
        'No last tenant found in repository, user has no valid last tenant.'
      );
      return false;
    }

    const userTenantIdList = await this._tenantHandlerService
      .getTenantList({
        authContext: AuthContextEnum.tenant
      })
      .then((userTenants) => userTenants?.map((userTenant) => userTenant?.id));

    if (!userTenantIdList.includes(tenantListFromRepo[0]?.id)) {
      /* istanbul ignore next */
      internalLogger?.debug?.(
        'The user does not have the last tenant id in the list of user tenants. Cleaning last tenants.'
      );
      this._tenantSetter.clearLastTenants();
      return false;
    }

    /* istanbul ignore next */
    internalLogger?.debug?.(
      'The user contains the last tenant id:',
      tenantListFromRepo[0]?.id
    );
    return true;
  }

  private _userHasTokenForTenantId(tenantId: string): boolean {
    const lastTenantSuffix = tenantId;
    const alreadyHasTokenForLastTenant =
      this._tenantHandlerService?.checkIfTenantIsStoredBySuffix(
        lastTenantSuffix
      );

    if (!alreadyHasTokenForLastTenant) {
      /* istanbul ignore next */
      internalLogger?.debug?.(
        'user does not has token for the tenant id: ',
        tenantId
      );
      return false;
    }

    /* istanbul ignore next */
    internalLogger?.debug?.('user has the token for the tenant: ', tenantId);
    return true;
  }

  private _rehydrateLastTenant(): void {
    const lastTenant =
      this._tenantRetriever.retrieveLastTenantsIdFromRepository()[0];

    const userHasTokenForLastTenant = this._userHasTokenForTenantId(
      lastTenant?.id
    );

    if (userHasTokenForLastTenant) {
      this._updateLastTenantOnRepositories(lastTenant);
    }

    if (this._queryParamsEnabled) {
      /* istanbul ignore next */
      internalLogger?.debug?.(
        'Setting the last tenant id in url and redirect:',
        lastTenant?.id
      );
      this._navigationService.redirect(
        `?t${lastTenant?.level}=${lastTenant?.id}`
      );
    }
  }

  /*
   *   This method triggers the setTenant in the Tenant Handler Service if applicable.
  TODO: This method should be placed in a better service or behavior.
   *  */
  public setTenantsFromInitialTenants = async (): Promise<boolean> => {
    // 1. Get the Candidates from the Initial Tenants;
    const candidateTenantList = this._tenantRetriever.retrieveInitialTenants();

    // 2. There is no tenant to set. Probably the Tenant Selector Operation will handle from now on.
    if (!candidateTenantList?.length) return false;

    // 3. Now, let's set the candidate tenant.
    const options = { reload: false };

    TabHelpers.removeTenantsFromQueryParams();

    for (const candidateTenant of candidateTenantList) {
      const userHasTokenForTenantCandidate = this._userHasTokenForTenantId(
        candidateTenant.id
      );

      if (!userHasTokenForTenantCandidate) {
        if (!this._queryParamsEnabled) {
          // @TODO check this code, it is a workaround for coptor portals
          // when the user access /loggedin in URL with tenant selected, the app clear the repositories with tenant and token information and can not recovery the context, so we force the auth process to recovery the app.
          const tenantIdFromToken = this._authTokenService.getTenantIdFromToken(
            this._authTokenService.getCurrentContext()
          );
          if (tenantIdFromToken) {
            /* istanbul ignore next */
            internalLogger?.debug?.(
              'The token already have a tenant, redirecting to the login path to recovery the app.'
            );

            return new Promise(() =>
              this._loginService.redirectToLogin({ forceLogin: true })
            );
          }
        }

        /* istanbul ignore next */
        internalLogger?.debug?.('Trying to set tenant:', candidateTenant.id);

        const isTenantSetSuccessfully =
          await this._tenantHandlerService?.setTenant(
            candidateTenant.id,
            candidateTenant.level,
            options,
            undefined
          );

        if (!isTenantSetSuccessfully) {
          /* istanbul ignore next */
          internalLogger?.debug?.(
            'Error in setTenant call, checking last tenant.'
          );

          const userHasValidLastTenant = await this._userHasValidLastTenant();
          if (userHasValidLastTenant) {
            this._rehydrateLastTenant();
          }

          return true;
        }
      }

      if (userHasTokenForTenantCandidate) {
        this._updateLastTenantOnRepositories(candidateTenant);
        return true;
      }
    }
  };
}
